import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

const KIND = { ASSOCIATION_AGREEMENT: "اتفاقية التعاون مع الجمعية", STUDENT_COMMITMENT: "وثيقة التزام التدريب التعاوني" };
const day = (v) => (v ? new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium" }).format(new Date(v)) : "—");

function Row({ label, value, ltr }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-3 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-bold text-slate-900 break-all" dir={ltr ? "ltr" : undefined}>{value}</span>
    </div>
  );
}

export default function VerifyContract() {
  const { number } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [state, setState] = useState({ loading: !!token, data: null });

  useEffect(() => {
    if (!token) { return; }
    api.get(`/contracts/verify/${number}`, { params: { token } })
      .then(({ data }) => setState({ loading: false, data }))
      .catch(() => setState({ loading: false, data: null }));
  }, [number, token]);

  const ok = state.data?.verified;
  return (
    <div className="min-h-screen bg-slate-50" dir="rtl" data-testid="verify-contract-page">
      <PublicNav />
      <section className="max-w-2xl mx-auto px-6 py-16">
        {state.loading ? (
          <div className="grid place-items-center py-24"><Loader2 className="h-10 w-10 animate-spin text-teal-700" /></div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden">
            <div className={`p-6 text-center text-white ${ok ? "bg-emerald-700" : token ? "bg-rose-700" : "bg-slate-600"}`} data-testid="verify-contract-status">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-xs font-bold mb-3"><ShieldCheck className="h-4 w-4" /> التحقق من الوثائق</div>
              {ok ? <CheckCircle2 className="h-14 w-14 mx-auto" /> : <XCircle className="h-14 w-14 mx-auto" />}
              <h1 className="mt-3 text-2xl font-heading font-black">{ok ? "الوثيقة موثّقة وسارية" : token ? "تعذّر التحقق من الوثيقة" : "هذه نسخة معاينة غير موقّعة"}</h1>
              <p className="mt-1 text-sm opacity-90" dir="ltr">{number}</p>
            </div>
            {state.data && (
              <div className="p-6" data-testid="verify-contract-details">
                <Row label="نوع الوثيقة" value={KIND[state.data.kind] || state.data.kind} />
                <Row label="سريان من" value={day(state.data.valid_from)} />
                <Row label="سريان إلى" value={day(state.data.valid_until)} />
                <Row label="تاريخ الإقفال" value={day(state.data.finalized_at)} />
                <Row label="بصمة الملف" value={(state.data.artifact_sha256 || "").slice(0, 24)} ltr />
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
